// src/frontend/src/pages/CustomScenarios.tsx - FIXED VERSION
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import { customScenarioService } from '../api/simulationApi';

const CustomScenarios: React.FC = () => {
  const navigate = useNavigate();
  const [customScenarios, setCustomScenarios] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  
  // 🔧 FIX 1: Use ref to prevent double submissions
  const isSubmittingRef = useRef<boolean>(false);

  useEffect(() => {
    fetchCustomScenarios();
  }, []);

  const fetchCustomScenarios = async () => {
    try {
      setIsLoading(true);
      const response = await customScenarioService.getCustomScenarios();
      setCustomScenarios(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch custom scenarios');
    } finally {
      setIsLoading(false);
    }
  };

  // 🔧 FIX 2: Simplified and protected against double submission
  const handleGenerateScenario = async (id: string) => {
    // Prevent double submission
    if (isSubmittingRef.current || isGenerating) {
      console.log('🔒 Prevented duplicate submission');
      return;
    }

    try {
      // Set submission guards
      isSubmittingRef.current = true;
      setError('');
      setSuccessMessage('');
      setIsGenerating(true);
      setGeneratingId(id);
      
      console.log('🚀 Generating scenario from template:', id);
      
      // Call the API to generate a scenario from the template
      const response = await customScenarioService.generateScenarioFromTemplate(id);
      
      console.log('✅ Scenario generation response:', response.data);
      
      // 🔧 FIX 3: Simplified navigation logic
      if (response.data?.data?.conversation?._id) {
        // If we have a conversation ID, navigate directly to it
        const conversationId = response.data.data.conversation._id;
        setSuccessMessage('Simulation created successfully! Starting conversation...');
        
        setTimeout(() => {
          navigate(`/simulations/${conversationId}`);
        }, 1000);
      } else {
        // Fallback - shouldn't happen with current backend but good to have
        setSuccessMessage('Scenario created successfully! Please check the simulations page.');
        setTimeout(() => {
          navigate('/coaching-sessions');
        }, 2000);
      }
      
    } catch (err: any) {
      console.error('❌ Error generating scenario:', err);
      
      setSuccessMessage('');
      const errorMessage = err.response?.data?.message || 'Failed to generate scenario';
      setError(errorMessage);
      
    } finally {
      // 🔧 FIX 4: Always reset submission state
      setTimeout(() => {
        setIsGenerating(false);
        setGeneratingId(null);
        isSubmittingRef.current = false;
      }, 1000);
    }
  };

  // Get difficulty badge color
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return 'bg-green-100 text-green-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'hard':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Format enum values for display
  const formatEnumValue = (value: string): string => {
    return value
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Custom Scenario Templates</h1>
          <Link
            to="/custom-scenarios/new"
            className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700"
          >
            Create New Template
          </Link>
        </div>
        
        {/* Error and Success Messages */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <p className="font-bold">Error</p>
            <p>{error}</p>
          </div>
        )}
        
        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            <p>{successMessage}</p>
          </div>
        )}
        
        {isLoading ? (
          <div className="text-center py-8">
            <p>Loading custom scenarios...</p>
          </div>
        ) : customScenarios.length === 0 ? (
          <div className="text-center py-8 bg-white rounded-lg shadow-md">
            <h3 className="text-lg font-medium text-gray-600 mb-2">No custom scenario templates yet</h3>
            <p className="text-gray-500 mb-4">Create your first custom scenario template to generate tailored simulations</p>
            <Link
              to="/custom-scenarios/new"
              className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700"
            >
              Create Template
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {customScenarios.map((scenario: any) => (
              <div 
                key={scenario._id} 
                className="bg-white rounded-lg shadow-md overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex justify-between items-start mb-2">
                    <h2 className="text-xl font-semibold">{scenario.title}</h2>
                    <span className={`px-2 py-1 text-xs rounded-full ${getDifficultyColor(scenario.difficulty)}`}>
                      {formatEnumValue(scenario.difficulty)}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 mb-4">{scenario.description}</p>
                  
                  <div className="mb-4">
                    <div className="flex items-center mb-1">
                      <span className="font-medium text-gray-700 mr-2">Industry:</span>
                      <span>{scenario.industry}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-medium text-gray-700 mr-2">Project Type:</span>
                      <span>{scenario.projectType}</span>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <h3 className="font-medium text-gray-700 mb-1">Stakeholder Types:</h3>
                    <div className="flex flex-wrap gap-1">
                      {scenario.stakeholderTypes.map((type: string) => (
                        <span 
                          key={type} 
                          className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                        >
                          {formatEnumValue(type)}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <h3 className="font-medium text-gray-700 mb-1">Challenge Types:</h3>
                    <div className="flex flex-wrap gap-1">
                      {scenario.challengeTypes.map((type: string) => (
                        <span 
                          key={type} 
                          className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full"
                        >
                          {formatEnumValue(type)}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <button
                      onClick={() => handleGenerateScenario(scenario._id)}
                      disabled={isGenerating}
                      className={`w-full py-2 px-4 rounded-md transition-colors ${
                        isGenerating && generatingId === scenario._id
                          ? 'bg-gray-300 cursor-not-allowed text-gray-600'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      {isGenerating && generatingId === scenario._id
                        ? 'Generating...'
                        : 'Generate Simulation from Template'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CustomScenarios;